import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, combineLatest, distinctUntilChanged, firstValueFrom, map, Observable, Subject, switchMap, take, tap } from 'rxjs';
import { RequestWrapperClass } from 'src/app/shared/services/request-wrapper.class';
import { AddUserTokenGQL, AddUserTokenInput, AddUserTokenResult, NotificationChannel, NotificationSettings, NotificationSettingsGQL, NotificationSettingsInput, RemindPinGQL, RemindUserApplicationPinResult, SetUserApartmentPinGQL, SetUserApartmentPinInput, SetUserApartmentPinResult, UpdateUserSettingsGQL, UpdateUserSettingsInput, UpdateUserSettingsResult } from '../generated/schema';

import {
    ActionPerformed,
    PushNotificationSchema,
    PushNotifications,
    Token,
} from '@capacitor/push-notifications'
import { Capacitor } from '@capacitor/core';
import { UserStateService } from 'src/app/shared/services/user-state.class';
import { FeatureSettingsService, ResidentSettings } from 'src/app/services/feature-settings.service';

@Injectable({
    providedIn: 'root',
})

export class UserSettingsService extends RequestWrapperClass implements OnDestroy {
    isPushNotificationsAvailable:boolean = Capacitor.isPluginAvailable('PushNotifications');

    public syncUnitsUpdates$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
    private checkOnMeEnabledUpdates$ = new BehaviorSubject<boolean | undefined>(undefined);

    constructor(
        private notificationSettingsService: NotificationSettingsGQL,
        private updateUserSettingsService: UpdateUserSettingsGQL,
        private remindPinService: RemindPinGQL,
        private setUnitPinService: SetUserApartmentPinGQL,
        private setUserTokenService: AddUserTokenGQL,
        public userStateService: UserStateService,
        private featureSettingsService: FeatureSettingsService,
    ) {
        super(userStateService);
    }

    getUserNotificationSettings(): Observable<NotificationSettings> {
        return this.createRetryableObserver<NotificationSettings>(async () => {
            // Get from InfluxDB only
            const residentSettings = await firstValueFrom(this.featureSettingsService.getResidentSettings());
            
            if (residentSettings?.notificationSettings) {
                // Convert from InfluxDB format to NotificationSettings format
                // InfluxDB stores channels in lowercase (e.g., "email", "push", "flash", "siren", "sms")
                const settings = residentSettings.notificationSettings;
                
                const mapChannel = (ch: string): NotificationChannel => {
                    const upperCh = ch.toUpperCase();
                    if (upperCh in NotificationChannel) {
                        return NotificationChannel[upperCh as keyof typeof NotificationChannel];
                    }
                    return NotificationChannel.Push; // Default fallback
                };
                
                return {
                    muteNotifications: settings.muteNotifications || false,
                    lowLevelNotificationChannels: (settings.lowLevelNotificationChannels || []).map(mapChannel) as NotificationChannel[],
                    mediumLevelNotificationChannels: (settings.mediumLevelNotificationChannels || []).map(mapChannel) as NotificationChannel[],
                    criticalLevelNotificationChannels: (settings.criticalLevelNotificationChannels || []).map(mapChannel) as NotificationChannel[],
                    energyConsumptionNotifications: settings.energyConsumptionNotifications || false,
                    // Defaults ON (unlike the sibling fields above) - severe weather push/banner is opt-out, not opt-in.
                    severeWeatherNotifications: settings.severeWeatherNotifications ?? true
                } as NotificationSettings;
            }

            // Return defaults if not found in InfluxDB
            return {
                __typename: 'NotificationSettings',
                muteNotifications: false,
                lowLevelNotificationChannels: [],
                mediumLevelNotificationChannels: [],
                criticalLevelNotificationChannels: [],
                energyConsumptionNotifications: false,
                severeWeatherNotifications: true
            };
        });
    }

    /**
     * Get package notification settings from InfluxDB
     */
    getPackageNotificationSettings(): Observable<{
        enabled: boolean;
        push: boolean;
        email: boolean;
        sms: boolean;
        visualAlert: boolean;
        audioAlert: boolean;
    }> {
        // Don't call loadAllSettings() here - it should be called once during app initialization
        // Calling it here causes unnecessary reloads and potential loops
        
        return this.featureSettingsService.getResidentSettings().pipe(
            map((settings) => {
                if (settings?.packageNotificationSettings) {
                    // Convert string format ("flash"/"siren") to boolean for UI
                    // Following current implementation: "flash" = true, null/undefined = false
                    const visualAlertValue = settings.packageNotificationSettings.visualAlert;
                    const audioAlertValue = settings.packageNotificationSettings.audioAlert;
                    const visualAlert = typeof visualAlertValue === 'string' ? visualAlertValue === 'flash' : false;
                    const audioAlert = typeof audioAlertValue === 'string' ? audioAlertValue === 'siren' : false;
                    
                    return {
                        enabled: settings.packageNotificationSettings.enabled ?? true,
                        push: settings.packageNotificationSettings.push ?? true,
                        email: settings.packageNotificationSettings.email ?? false,
                        sms: settings.packageNotificationSettings.sms ?? false,
                        visualAlert: visualAlert,
                        audioAlert: audioAlert
                    };
                }
                // Return defaults if not found
                return {
                    enabled: true,
                    push: true,
                    email: false,
                    sms: false,
                    visualAlert: false,
                    audioAlert: true
                };
            }),
            // Prevent duplicate emissions with same values
            distinctUntilChanged((prev, curr) => 
                JSON.stringify(prev) === JSON.stringify(curr)
            )
        );
    }

    /**
     * Update package notification settings in InfluxDB
     */
    updatePackageNotificationSettings(settings: {
        enabled: boolean;
        push: boolean;
        email: boolean;
        sms: boolean;
        visualAlert: boolean;
        audioAlert: boolean;
    }): Observable<boolean> {
        return this.createRetryableObserver<boolean>(async () => {
            // Get current resident settings
            const currentSettings = await firstValueFrom(this.featureSettingsService.getResidentSettings()) || {};
            
            // Convert boolean to string format following current implementation pattern
            // visualAlert: true -> "flash", false -> undefined
            // audioAlert: true -> "siren", false -> undefined
            // This matches how Flash and Siren are stored for consumption alerts
            const visualAlertValue = settings.visualAlert ? 'flash' : undefined;
            const audioAlertValue = settings.audioAlert ? 'siren' : undefined;
            
            // Explicitly set only valid ResidentSettings fields to avoid copying unwanted fields
            const updatedSettings: ResidentSettings = {
                earlyAccess: currentSettings?.earlyAccess,
                notificationSettings: currentSettings?.notificationSettings,
                packageNotificationSettings: {
                    enabled: settings.enabled,
                    push: settings.push,
                    email: settings.email,
                    sms: settings.sms,
                    visualAlert: visualAlertValue,
                    audioAlert: audioAlertValue
                }
            };

            // Save to InfluxDB only
            return await this.featureSettingsService.updateResidentSettings(updatedSettings);
        });
    }

    getSyncUnitsSetting(): Observable<boolean> {

        return combineLatest([
            this.createRetryableObserver<boolean>(async () => {
                const response = await this.notificationSettingsService.fetch().toPromise();
                return <boolean>response!.data!.userSettings.syncClimate || false;
            }),
            this.syncUnitsUpdates$.asObservable()
        ]).pipe(
            map(([queryRes, updatesRes]) => typeof updatesRes === 'boolean' ? updatesRes : queryRes)
        );
    }

    updateSyncUnitsSetting(isSync: boolean) {
        return this.createRetryableObserver<boolean>(async () => {
            const variables: { input: UpdateUserSettingsInput } = {
                input: {
                    syncClimate: isSync,
                }
            };

            const response = await this.updateUserSettingsService.mutate(variables).toPromise();

            return response?.data?.updateUserSettings == UpdateUserSettingsResult.UserSettingsUpdated ? true : false;
        }).pipe(
            tap((result) => {
                // manual updating value
                if (result) this.syncUnitsUpdates$.next(isSync)
            })
        );
    }

    getCheckOnMeEnabledSetting(): Observable<boolean> {

        return combineLatest([
            this.createRetryableObserver<boolean>(async () => {
                const response = await this.notificationSettingsService.fetch().toPromise();
                return <boolean>response!.data!.userSettings.checkOnMeEnabled || false;
            }),
            this.checkOnMeEnabledUpdates$.asObservable()
        ]).pipe(
            map(([queryRes, updatesRes]) => typeof updatesRes === 'boolean' ? updatesRes : queryRes)
        );
    }

    updateCheckOnMeEnabledSetting(enabled: boolean) {
        return this.createRetryableObserver<boolean>(async () => {
            const variables: { input: UpdateUserSettingsInput } = {
                input: {
                    checkOnMeEnabled: enabled,
                }
            };

            const response = await this.updateUserSettingsService.mutate(variables).toPromise();

            return response?.data?.updateUserSettings == UpdateUserSettingsResult.UserSettingsUpdated ? true : false;
        }).pipe(
            tap((result) => {
                // manual updating value
                if (result) this.checkOnMeEnabledUpdates$.next(enabled)
            })
        );
    }

    updateUserSettings(muteNotifications: boolean,consumptionAlert: boolean, criticalLevelNotificationChannels: NotificationChannel[], lowLevelNotificationChannels: NotificationChannel[], mediumLevelNotificationChannels: NotificationChannel[]): Observable<boolean> {
        return this.createRetryableObserver<boolean>(async () => {
            // Get current resident settings
            const currentSettings = await firstValueFrom(this.featureSettingsService.getResidentSettings()) || {};
            
            // Update notificationSettings in resident settings (InfluxDB)
            // Convert NotificationChannel enum values to lowercase strings for InfluxDB
            // NotificationChannel enum values are like 'EMAIL', 'PUSH', etc., so we convert to lowercase
            const mapChannelToString = (ch: NotificationChannel): string => {
                return String(ch).toLowerCase();
            };
            
            // Explicitly set only valid ResidentSettings fields to avoid copying unwanted fields like syncClimate
            const updatedSettings: ResidentSettings = {
                earlyAccess: currentSettings?.earlyAccess,
                notificationSettings: {
                    muteNotifications: muteNotifications,
                    lowLevelNotificationChannels: lowLevelNotificationChannels.map(mapChannelToString),
                    mediumLevelNotificationChannels: mediumLevelNotificationChannels.map(mapChannelToString),
                    criticalLevelNotificationChannels: criticalLevelNotificationChannels.map(mapChannelToString),
                    energyConsumptionNotifications: consumptionAlert
                },
                packageNotificationSettings: currentSettings?.packageNotificationSettings
            };

            // Save to InfluxDB only
            return await this.featureSettingsService.updateResidentSettings(updatedSettings);
        });
    }

    // Separate from updateUserSettings() rather than widening its 5-arg signature.
    // Spreads currentSettings wholesale (not just earlyAccess/notificationSettings/
    // packageNotificationSettings) - updateResidentSettings() also overwrites the
    // local residentSettings$ cache with this object, so a partial one here would
    // drop unrelated fields (phoneAsKeyEnabled, surveyTester, etc.) from the cache.
    updateSevereWeatherAlertSetting(enabled: boolean): Observable<boolean> {
        return this.createRetryableObserver<boolean>(async () => {
            const currentSettings = await firstValueFrom(this.featureSettingsService.getResidentSettings()) || {};

            const updatedSettings: ResidentSettings = {
                ...currentSettings,
                notificationSettings: {
                    ...currentSettings?.notificationSettings,
                    severeWeatherNotifications: enabled
                },
            };

            return await this.featureSettingsService.updateResidentSettings(updatedSettings);
        });
    }

    remindPin(): Observable<boolean> {
        return this.createRetryableObserver<boolean>(async () => {

            const response = await this.remindPinService.mutate().toPromise();

            return response?.data?.remindUserApplicationPin == RemindUserApplicationPinResult.UserApplicationPinSentToEmail ? true : false;
        });
    }

    setUnitPin(pin: string): Observable<boolean> {
        return this.createRetryableObserver<boolean>(async () => {
            const variables: { input: SetUserApartmentPinInput } = {
                input: {
                    pin: pin,
                }
            };

            const response = await this.setUnitPinService.mutate(variables).toPromise();

            return response?.data?.setUserApartmentPin == SetUserApartmentPinResult.UserApartmentPinSet ? true : false;
        });
    }

    registerDevice() {
        if(!this.isPushNotificationsAvailable) return;

        PushNotifications.requestPermissions().then(async (permission) => {
            if (permission.receive == "granted") {
                //Register device
                await PushNotifications.register();

            } else {
                console.error('No Permission for Push-Notifications!');
            }
        });

        PushNotifications.addListener('registration', (token: Token) => {
            //Send to BackEnd token
            this.setDeviceToken(token.value).subscribe((res) => {
                if (res) {
                    console.log("FCM Token registered");
                } else {
                    console.log("FCM Token NOT registered");
                }
            });
        });
    }

    setDeviceToken(token: string): Observable<boolean> {
        return this.createRetryableObserver<boolean>(async () => {
            const variables: { input: AddUserTokenInput } = {
                input: {
                    fcmToken: token,
                }
            };

            const response = await this.setUserTokenService.mutate(variables).toPromise();

            return response?.data?.addUserToken == AddUserTokenResult.UserTokenAdded ? true : false;
        });
    }
    ngOnDestroy(): void {
    }
}
